import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getArticles } from "../api/articlesApi";
import { getFeaturedTopics } from "../api/featuredApi";
import { getPinnedHomeCards } from "../api/pinnedHomeApi";
import { API_ORIGIN } from "../api/authApi";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";
import ScrollToTopButton from "../components/ScrollToTopButton";
import TypingBanner from "../components/TypingBanner";
import Reveal from "../components/Reveal";
import HeroPosterCard from "../components/HeroPosterCard";
import darichePlaceholderImage from "../assets/images/dariche-logo.png";

// Shown in a hero-poster slot the admin hasn't picked a card for yet (see
// AdminFeaturedPage.js). Position 2 is the center slot and keeps the
// larger H1 treatment even as a placeholder, matching the layout once a
// real card is picked.
const PLACEHOLDER_CARDS = {
  1: { kicker: "به‌زودی", title: ["پروندهٔ", "بعدی"], subtitle: "در شمارهٔ آینده" },
  2: { kicker: "به‌زودی", title: ["پروندهٔ", "ویژه"], subtitle: "در شمارهٔ آینده" },
  3: { kicker: "به‌زودی", title: ["پروندهٔ", "دیگر"], subtitle: "در شمارهٔ آینده" },
};

// Articles have a dedicated card thumbnail (see ArticleFormPage.js's
// image field) — falls back to the site logo when the admin hasn't set
// one, same placeholder as before this field existed.
function articleImage(article) {
  return article.image_path ? `${API_ORIGIN}${article.image_path}` : darichePlaceholderImage;
}

function extractPlainText(html) {
  const container = document.createElement("div");
  container.innerHTML = html || "";
  return container.textContent || "";
}

// Section A: the public site. No account needed to land here or browse —
// this is what every visitor sees first at "/".
function HomePage() {
  const [articles, setArticles] = useState([]);
  const [pinnedCards, setPinnedCards] = useState([]);
  const [featured, setFeatured] = useState({});

  useEffect(() => {
    // Public, unauthenticated fetch — proves logged-out visitors see
    // whatever admins have published, no account required.
    getArticles()
      .then(setArticles)
      .catch(() => setArticles([]));

    // Admin-pinned نشریه/شماره cards (see AdminPinnedHomePage.js) that
    // show up ahead of the articles above, in the same "مقاله‌های
    // منتخب" row.
    getPinnedHomeCards()
      .then(setPinnedCards)
      .catch(() => setPinnedCards([]));

    getFeaturedTopics()
      .then((list) => {
        const byPosition = {};
        for (const card of list) {
          byPosition[card.position] = card;
        }
        setFeatured(byPosition);
      })
      .catch(() => setFeatured({}));
  }, []);

  // Pinned نشریه/شماره cards come first (in the admin's chosen order),
  // then every article in its existing (newest-first) order — all
  // normalized to the same shape so the "مقاله‌های منتخب" row below can
  // render them identically regardless of source.
  const featureItems = [
    ...pinnedCards.map((card) => ({
      key: `${card.kind}-${card.id}`,
      href:
        card.kind === "issue_topic"
          ? `/issues/${card.issue_id}/topics/${card.id}`
          : `/publications/${card.publication_slug}/topics/${card.id}`,
      image: card.image_path ? `${API_ORIGIN}${card.image_path}` : darichePlaceholderImage,
      title: card.title_fa || card.title_en,
      excerpt: card.description_fa || card.description_en,
    })),
    ...articles.map((article) => ({
      key: `article-${article.id}`,
      href: `/articles/${article.id}`,
      image: articleImage(article),
      title: article.title_fa || article.title_en,
      excerpt: extractPlainText(article.body_fa || article.body_en),
    })),
  ];

  return (
    <div className="site">
      <SiteHeader />

      <main>
        {/*
          Full-viewport landing view — the quote is the first (and only)
          thing visible on load; the poster cards and everything after sit
          below the fold, revealed by scrolling. The bounce cue at the
          bottom is the affordance for that.
        */}
        <section className="home-hero">
          <TypingBanner />
          <span className="home-hero-scroll-cue" aria-hidden="true">
            <span className="home-hero-scroll-cue-arrow">↓</span>
          </span>
        </section>

        {/*
          Homepage hero — three poster-style cards side by side, each an
          outer frame whose top edge breaks around a centered kicker (the
          دریچه ۱۲ special-feature spread's layout). Content is admin-picked
          via AdminFeaturedPage.js (/admin/featured), one card per slot from
          any issue's کارت‌ها; a slot with no pick shows a "به‌زودی"
          placeholder instead. Every card is blurred until hovered — see
          .hero-poster:hover.
        */}
        <section className="hero-poster-row">
          {[1, 2, 3].map((position) => {
            const card = featured[position];
            const titleTag = position === 2 ? "h1" : "h2";

            if (!card) {
              return <HeroPosterCard key={position} titleTag={titleTag} {...PLACEHOLDER_CARDS[position]} />;
            }

            return (
              <Link
                key={position}
                to={`/issues/${card.issue_id}/topics/${card.topic_id}`}
                className="pub-topic-card-link"
              >
                <HeroPosterCard
                  titleTag={titleTag}
                  kicker={card.category}
                  title={card.title_fa || card.title_en}
                  byline={card.byline_fa || card.byline_en}
                  subtitle={card.description_fa || card.description_en}
                  imageSrc={card.image_path ? `${API_ORIGIN}${card.image_path}` : undefined}
                  imageAlt={card.title_fa || card.title_en}
                  showRule
                />
              </Link>
            );
          })}
        </section>

        <div className="dimension-divider">
          <span className="dimension-line" />
          <span className="dimension-label">مقاله‌های منتخب</span>
          <span className="dimension-line" />
        </div>

        <section className="feature-section">
          {featureItems.length === 0 && <p className="admin-empty">هنوز موردی برای نمایش ثبت نشده است.</p>}

          {featureItems.length > 0 && (
            <div className="feature-row">
              {featureItems.map((item, index) => (
                <Reveal key={item.key} delay={index * 80}>
                  <Link to={item.href} className="pub-topic-card-link feature-simple-card-link">
                    <article className="feature-simple-card">
                      <span className="corner corner-tr" />
                      <span className="corner corner-tl" />
                      <span className="corner corner-br" />
                      <span className="corner corner-bl" />

                      <div className="feature-simple-card-image-frame">
                        <img className="feature-simple-card-image" src={item.image} alt={item.title} />
                      </div>

                      <div className="feature-simple-card-content">
                        <div className="feature-simple-card-rule">
                          <span className="feature-simple-card-rule-dot" aria-hidden="true">
                            ❖
                          </span>
                          <span className="feature-simple-card-rule-line" />
                        </div>

                        <h2>{item.title}</h2>
                        <p>{item.excerpt}</p>
                      </div>
                    </article>
                  </Link>
                </Reveal>
              ))}
            </div>
          )}
        </section>

      </main>

      <SiteFooter />
      <ScrollToTopButton />
    </div>
  );
}

export default HomePage;
